import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { useEffect, useState, type ReactNode } from "react";
import { AppStoreContext, type AppStores } from "./app-context";
import { FeatureRegistryStore } from "./feature-registry/feature-registry-store";
import { shellFeatureDescriptors } from "./feature-registry/shell-feature-registry";
import { LocaleStore } from "./locale-store";
import { NotificationStore } from "./notification-store";
import { SessionStore } from "./session-store";
import { ShellStatusStore } from "./shell-status-store";
import { ThemeStore } from "./theme-store";
import { WorkspaceStore } from "./workspace/workspace-store";
import { ApiClient } from "../platform/api-client";
import { SessionGate } from "./session/session-gate";
import { MissionSelectionStore } from "../features/operations/mission-selection-store";
import { OperationalContextStore } from "../features/operations/operational-context-store";
import { primaryScreenForDevice } from "./client-device";
import { useResponsiveDevice } from "./use-responsive-device";
import type { ViewDeviceType } from "./feature-registry/feature-contract";
import { TerminologyProvider } from "../features/terminology/terminology-runtime";

export interface AppProvidersProps {
  children: ReactNode;
  stores?: AppStores;
}

export function createAppStores(clientDevice?: ViewDeviceType): AppStores {
  const session = new SessionStore(clientDevice);
  const api = new ApiClient({ csrfTokenProvider: () => session.csrfToken });
  const features = new FeatureRegistryStore(
    shellFeatureDescriptors,
    session,
    session.clientDevice,
    primaryScreenForDevice(session.clientDevice),
  );
  const missionSelection = new MissionSelectionStore();
  const operationalContext = new OperationalContextStore(
    api,
    (missionId, organizationUnitId) => {
      if (missionId === null) missionSelection.clear();
      else missionSelection.select(missionId, organizationUnitId);
    },
    // Read live, not captured: the session re-narrows its permissions whenever
    // the operator switches work context.
    () => session.permissions.has("account_read"),
  );
  // Stores are shell-scoped and deliberately survive logout/login. Bind the
  // operational projection synchronously to the authenticated principal so a
  // new account cannot render or request the prior account's selected incident.
  reaction(
    () => session.identity?.principalId ?? null,
    (principalId) => operationalContext.bindPrincipal(principalId),
    { fireImmediately: true },
  );
  reaction(
    () => [
      session.activeContext?.roleCode ?? null,
      session.activeOrganizationUnit?.id ?? null,
    ] as const,
    ([roleCode, organizationUnitId]) => {
      operationalContext.bindOperationalScope(roleCode, organizationUnitId);
    },
    { fireImmediately: true },
  );
  return {
    api,
    features,
    locale: new LocaleStore(),
    missionSelection,
    notifications: new NotificationStore(api),
    operationalContext,
    session,
    status: new ShellStatusStore(),
    theme: new ThemeStore(),
    workspace: new WorkspaceStore(undefined, (viewId) => {
      if (viewId === "shell_status") {
        return { allowClose: false, allowDetach: false, allowMove: false, allowMultiple: false, minHeight: 240, minWidth: 320 };
      }
      const resolution = features.resolve(viewId, "panel");
      if (resolution.status !== "allowed") return null;
      const { policy } = resolution.descriptor;
      return {
        allowClose: policy.allowClose,
        allowDetach: policy.allowDetach,
        allowMove: policy.allowMove,
        allowMultiple: policy.allowMultiple,
        minHeight: policy.minSize.height,
        minWidth: policy.minSize.width,
      };
    }),
  };
}

export const AppProviders = observer(function AppProviders({
  children,
  stores: providedStores,
}: AppProvidersProps) {
  const [stores] = useState(() => providedStores ?? createAppStores());

  useEffect(() => {
    stores.theme.apply();
    void stores.locale.load();
    void stores.workspace.load();
    return stores.status.start();
  }, [stores]);

  useResponsiveDevice(stores);

  if (!stores.locale.messages) {
    return <p role="status">Loading interface…</p>;
  }

  return (
    <AppStoreContext.Provider value={stores}>
      <SessionGate>
        <TerminologyProvider api={stores.api} locale={stores.locale.code}>
          {children}
        </TerminologyProvider>
      </SessionGate>
    </AppStoreContext.Provider>
  );
});
